import "./UsersAndPermissions.scss";

import { Alert, Button, ChatIcon, Flex, Popup, Text, Tooltip } from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { TeamsUser } from "@teams/types";
import { ChannelAccountDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { useTeamsMembers } from "../actions/useTeamsMembers";
import { isError } from "../services/ChannelApi";
import { useTeamsContext } from "../state/TeamsContextProvider";
import { useTheme } from "../state/ThemeProvider";
import { Identity } from "./Identity";
import { PollyIcon } from "./PollyIcon";
import { TeamsLoader } from "./TeamsLoader";
import { UserDropdown } from "./UserDropdown";

export const UsersAndPermissions: React.FC<{
  canOwn: boolean;
  owners: TeamsUser[];
  ownersHeader?: React.ReactNode;
  ownersHeaderDescription?: React.ReactNode;
  maxUsers?: number;
  showUsers?: boolean;
  users?: TeamsUser[];
  usersHeader?: React.ReactNode;
  usersHeaderDescription?: React.ReactNode;
  onUpgradeRequest?: () => void;
  onAddOwner: (account: ChannelAccountDTO) => Promise<Error | void>;
  onRemoveOwner: (owner: TeamsUser) => Promise<Error | void>;
  onAddUser?: (account: ChannelAccountDTO) => Promise<Error | void>;
  onRemoveUser?: (user: TeamsUser) => Promise<Error | void>;
}> = ({
  ownersHeader,
  ownersHeaderDescription,
  usersHeader,
  usersHeaderDescription,
  canOwn,
  owners,
  showUsers,
  maxUsers,
  users,
  onAddOwner,
  onRemoveOwner,
  onAddUser,
  onRemoveUser,
  onUpgradeRequest,
}) => {
  const { teamsContext, hostTab } = useTeamsContext();
  const { loadTeamsMembers, isTeamsMembersLoading, teamMembers } = useTeamsMembers();
  const { themeName } = useTheme();
  const isPersonalPage = hostTab === "dashboard";

  useEffect(() => {
    if (teamsContext) {
      loadTeamsMembers(teamsContext);
    }
  }, [loadTeamsMembers, teamsContext]);

  return (
    <div className={`users-and-permissions users-and-permissions__theme-${themeName}`}>
      {ownersHeader && (
        <Text
          content={ownersHeader}
          color="brand"
          weight="bold"
          as="h3"
          className="users-and-permissions__header"
        />
      )}
      {ownersHeaderDescription && (
        <Text
          content={ownersHeaderDescription}
          as="div"
          size="small"
          weight="light"
          className="users-and-permissions__header-info"
        />
      )}

      {canOwn && (
        <>
          {maxUsers !== undefined && users !== undefined && users.length >= maxUsers && (
            <Alert
              warning
              content="Purchase more licenses to add more owners to this subscription"
              styles={{ marginBottom: ".5rem" }}
              actions={
                canOwn
                  ? [
                      {
                        key: "upgrade",
                        primary: true,
                        content: "Upgrade",
                        onClick: onUpgradeRequest,
                      },
                    ]
                  : []
              }
            />
          )}

          {!isPersonalPage &&
            (maxUsers === undefined || users === undefined || users.length < maxUsers) && (
              <TeamsLoader
                isLoading={isTeamsMembersLoading}
                loadingMessage="Loading Teams Users ..."
                size="small"
                labelPosition="end"
              >
                <div className="users-and-permissions__user-add">
                  <AddUserSelection
                    members={teamMembers.filter(
                      m => !owners.some(o => o.aadObjectId === m.aadObjectId)
                    )}
                    teamsContext={teamsContext as app.Context}
                    addButtonContent="Add User"
                    disabled={
                      maxUsers !== undefined && users !== undefined && users.length >= maxUsers
                    }
                    onAddUser={onAddOwner}
                  />
                </div>
              </TeamsLoader>
            )}
          {isPersonalPage && <PersonalPageAddUserWarning />}
        </>
      )}

      <Flex column hAlign="stretch" className="users-and-permissions__owner-list">
        {owners
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(owner => (
            <UserRow
              key={owner.aadObjectId}
              user={owner}
              showRemove={canOwn}
              allowRemove={canOwn && owners.length > 1}
              removeWarning={
                owner.aadObjectId !== teamsContext?.user?.id ? (
                  <>
                    Are you sure you wish to remove <b>{owner.name}</b> as an owner?
                  </>
                ) : (
                  <>
                    Are you sure you want to remove <b>yourself</b> as an owner? You will no longer
                    be able to manage user licenses.
                  </>
                )
              }
              onRemove={onRemoveOwner}
            />
          ))}
      </Flex>

      {usersHeader && (
        <>
          <Text
            content={usersHeader}
            color="brand"
            weight="bold"
            as="h3"
            className="users-and-permissions__header"
          />
          <Text
            content={usersHeaderDescription}
            as="div"
            size="small"
            weight="light"
            className="users-and-permissions__header-info"
          />
        </>
      )}

      {showUsers && users && (
        <>
          {maxUsers !== undefined && users.length >= maxUsers && (
            <Alert
              warning
              content="Purchase more licenses to add more users to this subscription"
              styles={{ marginBottom: ".5rem" }}
              actions={
                canOwn
                  ? [
                      {
                        key: "upgrade",
                        primary: true,
                        content: "Upgrade",
                        onClick: onUpgradeRequest,
                      },
                    ]
                  : []
              }
            />
          )}
          {!isPersonalPage &&
            canOwn &&
            onAddUser &&
            (maxUsers === undefined || users.length < maxUsers) && (
              <TeamsLoader
                isLoading={isTeamsMembersLoading}
                loadingMessage="Loading Users ..."
                size="small"
                labelPosition="end"
              >
                <div className="users-and-permissions__user-add">
                  <AddUserSelection
                    members={teamMembers.filter(
                      m => !users.some(u => u.aadObjectId === m.aadObjectId)
                    )}
                    addButtonContent="Add User"
                    teamsContext={teamsContext as app.Context}
                    onAddUser={onAddUser}
                    disabled={maxUsers !== undefined && users.length >= maxUsers}
                  />
                </div>
              </TeamsLoader>
            )}
          {isPersonalPage && <PersonalPageAddUserWarning />}

          <Flex column hAlign="stretch" className="users-and-permissions__user-list">
            {users
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(user => (
                <UserRow
                  key={user.aadObjectId}
                  user={user}
                  showRemove={canOwn}
                  allowRemove={!owners.some(owner => owner.aadObjectId === user.aadObjectId)}
                  removeWarning={
                    <>
                      Are you sure you wish to remove <b>{user.name}</b>? This will disable all
                      items they own that use this subscription!
                    </>
                  }
                  onRemove={onRemoveUser}
                />
              ))}
          </Flex>
        </>
      )}
    </div>
  );
};

type UserRowProps = {
  user: TeamsUser;
  allowRemove: boolean;
  showRemove: boolean;
  removeWarning: React.ReactNode;
  onRemove?: (user: TeamsUser) => Promise<void | Error>;
};

const UserRow: React.FC<UserRowProps> = ({
  user,
  allowRemove,
  showRemove,
  onRemove,
  removeWarning,
}) => {
  const [confirmDelete, setConfirmDelete] = useState<boolean>();
  const [isRemoving, setIsRemoving] = useState<boolean>();
  const [removeError, setRemoveError] = useState<string>();

  return (
    <Flex className="users-and-permissions__user-row">
      <Flex.Item grow={1}>
        <div>
          <Identity name={user.name} subname={user.upn} />
        </div>
      </Flex.Item>

      <Flex.Item push>
        <div>
          <Button
            data-tname="open-chat-with-user"
            icon={<ChatIcon />}
            iconOnly
            disabled={!user.upn}
            text
            onClick={() => {
              app.openLink(`https://teams.microsoft.com/l/chat/0/0?users=${user.upn}`);
            }}
          />

          {showRemove && onRemove && (
            <Tooltip content="Remove user">
              <Popup
                open={confirmDelete}
                onOpenChange={(_e, props) => setConfirmDelete(props?.open)}
                content={
                  <Flex column style={{ maxWidth: "300px" }} gap="gap.small">
                    <Alert content={removeWarning} warning />
                    <Button
                      data-tname="confirm-remove-user"
                      content="Confirm Remove"
                      fluid
                      primary
                      disabled={isRemoving}
                      loading={isRemoving}
                      onClick={async () => {
                        setRemoveError(undefined);
                        setIsRemoving(true);
                        const result = await onRemove(user);
                        setIsRemoving(false);
                        if (isError(result)) {
                          setRemoveError(result.message);
                        } else {
                          setConfirmDelete(false);
                        }
                      }}
                    />
                    {removeError && <Alert danger content={removeError} attached="bottom" />}
                  </Flex>
                }
              >
                <Button
                  className="trash-icon-button"
                  data-tname="remove-user"
                  iconOnly
                  text
                  disabled={!allowRemove || isRemoving}
                  loading={isRemoving}
                  icon={<PollyIcon icon="trash-icon" inheritColor />}
                  onClick={() => setConfirmDelete(true)}
                />
              </Popup>
            </Tooltip>
          )}
        </div>
      </Flex.Item>
    </Flex>
  );
};

type AddUserSelectionProps = {
  members: ChannelAccountDTO[];
  teamsContext: app.Context;
  addButtonContent: React.ReactNode;
  disabled?: boolean;
  onAddUser: (account: ChannelAccountDTO) => Promise<void | Error>;
};

const AddUserSelection: React.FC<AddUserSelectionProps> = ({
  teamsContext,
  members,
  onAddUser,
  disabled,
  addButtonContent,
}) => {
  const [selectedAddUser, setSelectedAddUser] = useState<ChannelAccountDTO>();
  const [isAdding, setIsAdding] = useState<boolean>();
  const [addError, setAddError] = useState<string>();

  return (
    <>
      <Flex vAlign="center" gap="gap.small">
        <UserDropdown
          users={members.map(m => ({ id: m.aadObjectId, name: m.name }))}
          value={selectedAddUser?.aadObjectId}
          onSelected={s => {
            const selectedUser = members?.find(u => u.aadObjectId === s);
            setSelectedAddUser(selectedUser);
            if (addError) {
              setAddError(undefined);
            }
          }}
        />

        <Tooltip
          content={`Only users that are members of the current team ${
            teamsContext.team?.displayName ? `(${teamsContext.team?.displayName})` : ""
          } are listed`}
          trigger={
            <span>
              <PollyIcon icon="info-icon" />
            </span>
          }
        />

        <Button
          data-tname={addButtonContent?.toString().replace(" ", "-")}
          disabled={!selectedAddUser || isAdding || disabled}
          loading={isAdding}
          primary
          icon={<PollyIcon icon="create-icon" inheritColor />}
          content={addButtonContent}
          onClick={async () => {
            if (!selectedAddUser) {
              return;
            }

            setAddError(undefined);

            setIsAdding(true);
            const result = await onAddUser(selectedAddUser);
            setIsAdding(false);

            if (isError(result)) {
              setAddError(result.message);
            }
          }}
        />
      </Flex>
      {addError && <Alert danger attached="bottom" content={addError} />}
    </>
  );
};

const PersonalPageAddUserWarning: React.FC = () => {
  return (
    <Text
      content="To add owners, please access this polly via the Polly tab within the channel, chat or meeting it was created in."
      color="red"
      weight="semibold"
      as="h3"
      className="users-and-permissions__header"
    />
  );
};
